/**
 * ParticipantsTestimonials.jsx
 * Displays a Swiper carousel of Google-style review cards.
 *
 * Data source priority:
 *   1. Admin-managed reviews from MongoDB  (/api/reviews — published only)
 *   2. Hardcoded static reviews (participantsData) as guaranteed fallback
 *
 * The isPreview prop is passed from CourseDetailPreview to disable
 * multi-column breakpoints in the admin preview pane.
 */

import { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper";

// Swiper styles
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/autoplay";

import participantsData from "./participantsData";
import ParticipantReview from "./ParticipantReview";

import "./testimonials.css";

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/$/, '');

const ParticipantsTestimonials = ({ isPreview = false }) => {
  const [reviews, setReviews] = useState(participantsData); // initialise with hardcoded data immediately

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        // Try to load admin-managed reviews from MongoDB
        const res = await fetch(`${API_URL}/api/reviews`);
        const json = await res.json();

        if (res.ok && json.success && Array.isArray(json.data) && json.data.length > 0) {
          setReviews(json.data);
        }
        // If no DB reviews exist, keep the hardcoded participantsData (already set as initial state)
      } catch (err) {
        // Network error — hardcoded data is already showing, nothing to do
        console.warn("Could not fetch DB reviews, using static fallback:", err.message);
      }
    };

    fetchReviews();
  }, []);

  return (
    <section id="testimonials">
      <h2>What Our Participants Say After Completing the Program</h2>
      <br />
      <p>
        These reviews are shared by participants who successfully completed
        the program and experienced our real-world, mentor-led approach firsthand.
      </p>

      <center style={{ marginBottom: "2rem" }}>
        <div className="google-trust-badge" data-aos="fade-up">
          <div className="google-logo-flat">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="18"
              height="18"
              aria-label="Google review"
            >
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
          </div>
          <div className="google-rating-info">
            <div className="google-stars">★★★★★</div>
            <p style={{ margin: 0 }}>Rated <strong>4.9 / 5</strong> by 120+ students</p>
          </div>
        </div>
      </center>

      <div className="container">
        <Swiper
          slidesPerView={1}
          spaceBetween={20}
          breakpoints={isPreview ? {} : {
            601: { slidesPerView: 2, spaceBetween: 20 },
            1025: { slidesPerView: 3, spaceBetween: 24 }
          }}
          pagination={{ clickable: true }}
          autoplay={{ delay: 4500, disableOnInteraction: false }}
          modules={[Pagination, Autoplay]}
          className="mySwiper"
        >
          {reviews.map(review => (
            <SwiperSlide key={review.id || review._id}>
              <ParticipantReview review={review} />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  );
};

export default ParticipantsTestimonials;
